import type { Filters, Group } from './types';

/**
 * Pure filter logic, kept free of React so it can be unit-tested directly.
 * `useFilters` wires this up to the URL and component state.
 */

/** Canonical order so the same selection always produces the same URL. */
const DAY_ORDER = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'other',
];

/** De-duplicates and orders days, so click order never changes the link. */
export function sortDays(days: string[]): string[] {
  return [...new Set(days)].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(a);
    const bi = DAY_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

/* ------------------------------------------------------------------ *
 * Search
 *
 * The pills are strict AND and binary — a group either matches or it does
 * not, so there is nothing to rank and the list stays alphabetical. Free text
 * is the opposite: "men" matches a group's name, another group's audience, and
 * a third group's description, and those are plainly not equally good answers.
 * So the search box both narrows the list and orders it.
 * ------------------------------------------------------------------ */

/**
 * Folds text down to something two spellings of the same word can agree on:
 * lowercase, accents stripped, punctuation reduced to spaces.
 *
 * That last part is what lets "men's" find "Mens LifeGroup", and "mom 2 mom"
 * find "Mom 2Mom Group" — real group names in this data.
 */
export function normalizeForSearch(text: string): string {
  return String(text ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // combining accents left by NFKD
    // Apostrophes are DELETED, not turned into spaces: "men's" has to fold to
    // "mens" so it matches the group actually named "Mens LifeGroup". Turning
    // it into a space gives "men s", which matches neither spelling.
    .replace(/['\u2019\u02bc`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Splits a query into terms. Every term must match — see `scoreGroup`. */
export function searchTerms(query: string): string[] {
  const normalized = normalizeForSearch(query);
  return normalized ? normalized.split(' ').filter(Boolean) : [];
}

/**
 * Shortest term allowed to match in the middle of a word.
 *
 * Mid-word matching is needed: "LifeGroup" is one word in Crossroads' style, so
 * "group" has to find "Sonshine Ladies LifeGroup". But unrestricted it also
 * means "men" matches "Women" and "art" matches "heart" — searching for men's
 * groups returned the women's group. Four characters keeps the useful case and
 * drops the coincidences.
 */
const MIN_INFIX_LENGTH = 4;

/**
 * How well one term matches one field, ignoring which field it was.
 *
 *   4  the field is exactly the term         "Aikido"       <- "aikido"
 *   3  the field starts with it              "Kingdom Dads" <- "kingdom"
 *   2  some word in the field starts with it "Better Man Defined" <- "man"
 *   1  it appears mid-word, if long enough   "LifeGroup"    <- "group"
 *   0  not there at all
 *
 * The gap between 2 and 1 is the important one: a word-start match is what a
 * person means by "matches", and a mid-word one is usually a coincidence.
 */
function positionScore(field: string, term: string): number {
  if (!field || !term) return 0;
  if (field === term) return 4;
  if (field.startsWith(term)) return 3;
  if (` ${field}`.includes(` ${term}`)) return 2;
  if (term.length >= MIN_INFIX_LENGTH && field.includes(term)) return 1;
  return 0;
}

/**
 * Which fields are searched, and how much a hit in each is worth.
 *
 * A name match beats a leader match beats a "when and where" match beats a
 * mention buried in the description — which is the order someone scanning the
 * page would rank them in themselves.
 */
function searchFields(group: Group): Array<{ text: string; weight: number }> {
  const leader = [group.leaderFirstName, group.leaderLastName].filter(Boolean).join(' ');
  return [
    { text: normalizeForSearch(group.name), weight: 10 },
    { text: normalizeForSearch(leader), weight: 6 },
    /*
      Audience is its own field rather than being lumped in with when-and-where,
      because exactness carries real meaning here: "Men" IS the term, while
      "Men & Women" merely contains it. Combined into one blob those two score
      the same, and a search for "men" put coed groups above the men's group.
    */
    { text: normalizeForSearch(group.audience ?? ''), weight: 4 },
    {
      text: normalizeForSearch(
        [group.location, group.meetingDay, group.meetingTime].filter(Boolean).join(' ')
      ),
      weight: 3,
    },
    { text: normalizeForSearch(group.descriptionText ?? ''), weight: 1 },
  ];
}

/**
 * Relevance of a group to a set of search terms.
 *
 * @returns a score, or `null` when the group does not match at all.
 *
 * EVERY term must land somewhere, which makes multi-word queries narrow rather
 * than widen — typing "womens tuesday" should find the Tuesday women's group,
 * not every women's group plus everything on a Tuesday. That matches how the
 * pills already behave, so the two halves of the UI do not disagree.
 */
export function scoreGroup(group: Group, terms: string[]): number | null {
  if (terms.length === 0) return 0;

  const fields = searchFields(group);
  let total = 0;

  for (const term of terms) {
    let best = 0;
    for (const field of fields) {
      best = Math.max(best, field.weight * positionScore(field.text, term));
    }
    if (best === 0) return null; // one miss disqualifies the group
    total += best;
  }

  // A group whose name contains the whole query as typed beats one that merely
  // collected the same words from scattered fields.
  const phrase = terms.join(' ');
  if (terms.length > 1 && normalizeForSearch(group.name).includes(phrase)) total += 15;

  return total;
}

export function applyFilters(groups: Group[], filters: Filters): Group[] {
  const terms = searchTerms(filters.query ?? '');

  const matched = groups
    .map((group, index) => ({ group, index, score: terms.length ? scoreGroup(group, terms) : 0 }))
    .filter((entry) => entry.score !== null);

  const narrowed = matched.filter(({ group }) => {
    // locationKey, not campusKey: house groups answer to "homes", not to the
    // campus that administers them.
    if (filters.location && group.locationKey !== filters.location) return false;

    // Day is multi-select and ORs: picking Tuesday and Thursday widens the
    // list to both, rather than narrowing it to groups that meet on both days.
    if (filters.days.length > 0 && !filters.days.includes(group.dayKey)) return false;

    if (filters.audience && group.audienceKey !== filters.audience) return false;
    if (filters.kidsMinistry && !group.kidsMinistry) return false;
    return true;
  });

  /*
    With no query there is nothing to rank, so the incoming order stands — the
    build already sorted it alphabetically with pinned and full groups last.

    With a query, best match leads, and ties fall back to that same incoming
    order via `index`. That is deliberate rather than lazy: it means the
    alphabetical order, the "full groups last" rule and the config `sortLast`
    pin all keep applying inside a band of equally relevant results, without
    being restated here. It also lets a pinned group climb when someone is
    plainly searching for it — the pin is a browsing preference, not a claim
    that the group is a worse answer.
  */
  if (terms.length === 0) return narrowed.map(({ group }) => group);

  return narrowed
    .sort((a, b) => (b.score as number) - (a.score as number) || a.index - b.index)
    .map(({ group }) => group);
}

/** Adds or removes a day; `null` clears the whole set. */
export function toggleDayIn(days: string[], value: string | null): string[] {
  if (value === null) return [];
  return days.includes(value)
    ? days.filter((day) => day !== value)
    : sortDays([...days, value]);
}
